import React, { ChangeEvent, SyntheticEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import Dropdown from '../../../App/Components/Dropdown/Dropdown'
import Spinner from '../../../App/Components/Spinner/Spinner'
import { useAppDispatch, useAppSelector } from '../../../App/Hooks/reduxHooks'
import { IUser } from '../../../User/DataStore/IUser'
import { getUsers } from '../../../User/Store/actions'
import { ITargetError, ITargetPeriod } from '../Client/ITarget'

import { IProps } from './IProps'
import style from './style.scss'
import { addError } from '../../../Common/ErrorHandling/store'

const Form = ({ onSubmit, target, errors }: IProps) => {
  const { t } = useTranslation()
  const history = useHistory()
  const dispatch = useAppDispatch()

  const [id, setId] = useState(target ? target.id : '')
  const [period, setPeriod] = useState<ITargetPeriod>(
    target ? target.period : 'monthly',
  )
  const [amount, setAmount] = useState<number>(target ? target.amount : 0)

  const [selectedPersonIds, setSelectedPersonIds] = useState(
    target ? target.persons.map(person => person.id) : [],
  )

  const TargetTypes: ITargetPeriod[] = ['daily', 'weekly', 'monthly', 'yearly']
  const persons = useAppSelector(state => state.users.list)

  useEffect(() => {
    if (persons && persons.length === 0) {
      dispatch(getUsers())
    }
  }, [])

  useEffect(() => {
    if (undefined !== target) {
      setId(target.id)
      setPeriod(target.period)
      setAmount(target.amount)
      const newSelectedPersonIds = target.persons.map(person => person.id)
      setSelectedPersonIds(newSelectedPersonIds)
    }
  }, [target])

  const onSelectPersonChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (true === event.target.checked) {
      setSelectedPersonIds([...selectedPersonIds, Number(event.target.value)])
    }

    if (false === event.target.checked) {
      setSelectedPersonIds(
        selectedPersonIds.filter(
          selectedPersonId => selectedPersonId !== Number(event.target.value),
        ),
      )
    }
  }

  if (persons && persons.length === 0) {
    return <Spinner />
  }

  const getCheckBoxes = () => {
    return (
      <>
        {persons
          .map((person: IUser) => (
            <>
              <label
                className="checkbox-row"
                key={`${person.id}`}
                htmlFor={`${person.id}`}>

                <input
                  className="checkbox"
                  onChange={event => onSelectPersonChange(event)}
                  type="checkbox"
                  checked={selectedPersonIds.includes(person.id)}
                  id={`${person.id}`}
                  value={`${person.id}`}
                />

                <div className="text"> 
                  {`${person.firstName ?? ''} ${person.lastName ?? ''}`}
                </div>
              </label>
            </>
          ))}
      </>
    )
  }

  return (
    <>
      <div
        className={
          errors.length > 0 ? style.validationError : style.noValidationError
        }>
        <ul>
          {errors.length > 0
            ? errors.map((error: ITargetError) => (
                <li>
                  {error.propertyPath}: {error.message}
                </li>
              ))
            : null}
        </ul>
      </div>
      <form
        className={'form-split'}
        onSubmit={(e: SyntheticEvent<HTMLFormElement>) => {
          e.preventDefault();
          let isError = false;

          const relevantPersons = persons.filter(person =>
            selectedPersonIds.includes(person.id),
          )

          if (relevantPersons.length === 0) {
            isError = true;

            dispatch(
              addError({
                title: 'Woops!',
                description: t(('You need to select at least 1 person.')),
                type: 'error',
                duration: 'long',
              }),
            )
          }

          const formattedPersons = relevantPersons.map(person => {
            return {
              id: person.id,
              name: `${person.firstName} ${person.lastName}`,
            }
          })

          if (!isError && onSubmit) {
            onSubmit({
              id,
              amount,
              period,
              persons: formattedPersons,
            })
          }
        }}>
        <label className={'full'}>
          <div className={'text'}>{t('Amount')}</div>
          <input
            className="input"
            type="number"
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              setAmount(Number(event.currentTarget.value))
            }
            value={amount}
          />
        </label>
        <Dropdown
          label={t('Period type')}
          onChange={(event: SyntheticEvent<HTMLSelectElement>) =>
            setPeriod(event.currentTarget.value as ITargetPeriod)
          }
          value={period}>
          {TargetTypes.map(targetType => (
            <option value={targetType}>{t(`${targetType}`)}</option>
          ))}
        </Dropdown>
        <div>{getCheckBoxes()}</div>
        <button type={'submit'} className={'button'}>
          {t('Save')}
        </button>
        <button
          type={'button'}
          className={'button dim'}
          onClick={() => history.goBack()}>
          {t('Cancel')}
        </button>
      </form>
    </>
  )
}

export default Form
